import { Feedback, FeedbackFormData } from '../types/feedback';
import { UserRole } from '../types/user';
import { getCurrentUser as getAuthUser } from './userService';

export const feedbackService = {
  // Get all feedback for a project
  getProjectFeedback: async (projectId: string): Promise<Feedback[]> => {
    try {
      // In a real app, this would be an API call
      // const response = await axios.get(`${API_BASE_URL}/projects/${projectId}/feedback`);
      // return response.data;
      
      // For now, return mock data
      return getMockFeedback(projectId);
    } catch (error) {
      console.error('Error fetching project feedback:', error);
      throw error;
    }
  },
  
  // Add new feedback
  addFeedback: async (feedbackData: FeedbackFormData): Promise<Feedback> => {
    try {
      // In a real app, this would be an API call
      // const response = await axios.post(`${API_BASE_URL}/feedback`, feedbackData);
      // return response.data;
      
      // For now, create a mock response
      const currentUser = getAuthUser();
      const newFeedback: Feedback = {
        id: `feedback_${Date.now()}`,
        projectId: feedbackData.projectId,
        content: feedbackData.content,
        type: feedbackData.type,
        createdAt: new Date().toISOString(),
        createdBy: {
          id: currentUser?._id || '1',
          name: currentUser?.name || 'Current User',
          role: currentUser?.role || UserRole.FACILITY_USER,
          organization: currentUser?.county || currentUser?.subCounty || currentUser?.facility || 'Unknown'
        },
        parentId: feedbackData.parentId,
        resolved: false
      };
      
      // In a real app, this would be handled by the backend
      mockFeedbackData.push(newFeedback);
      
      return newFeedback;
    } catch (error) {
      console.error('Error adding feedback:', error);
      throw error;
    }
  },
  
  // Update feedback (mark as resolved, etc.)
  updateFeedback: async (feedbackId: string, updates: Partial<Feedback>): Promise<Feedback> => {
    try {
      // In a real app, this would be an API call
      // const response = await axios.patch(`${API_BASE_URL}/feedback/${feedbackId}`, updates);
      // return response.data;
      
      // For now, update mock data
      const feedbackIndex = mockFeedbackData.findIndex(f => f.id === feedbackId);
      if (feedbackIndex >= 0) {
        mockFeedbackData[feedbackIndex] = {
          ...mockFeedbackData[feedbackIndex],
          ...updates
        };
        return mockFeedbackData[feedbackIndex];
      }
      throw new Error('Feedback not found');
    } catch (error) {
      console.error('Error updating feedback:', error);
      throw error;
    }
  },
  
  // Get current user info - in a real app, this would come from auth service
  getCurrentUser: () => {
    const user = getAuthUser();
    if (!user) return null;
    
    return {
      id: user._id,
      name: user.name,
      role: user.role,
      organization: user.county || user.subCounty || user.facility || 'Unknown',
      county: user.county,
      subCounty: user.subCounty,
      facility: user.facility
    };
  }
};

// Mock data for development
let mockFeedbackData: Feedback[] = [
  {
    id: 'feedback1',
    projectId: 'project1',
    content: 'When will the facility be ready to implement this intervention?',
    type: 'question',
    createdAt: '2025-07-28T09:00:00Z',
    createdBy: {
      id: '2',
      name: 'Alice Johnson',
      role: UserRole.SUBCOUNTY_USER,
      organization: 'Nairobi East Subcounty'
    },
    resolved: false
  },
  {
    id: 'feedback2',
    projectId: 'project1',
    content: 'We need more supplies before we can fully implement this project.',
    type: 'concern',
    createdAt: '2025-07-29T10:15:00Z',
    createdBy: {
      id: '3',
      name: 'Bob Smith',
      role: UserRole.FACILITY_USER,
      organization: 'Main Hospital'
    },
    resolved: false
  },
  {
    id: 'feedback3',
    projectId: 'project1',
    content: 'I suggest we include community health workers in the next phase.',
    type: 'suggestion',
    createdAt: '2025-07-30T14:30:00Z',
    createdBy: {
      id: '4',
      name: 'Carol Williams',
      role: UserRole.COUNTY_USER, // Using COUNTY_USER for WIT users
      organization: 'Health Department'
    },
    resolved: false
  },
  {
    id: 'feedback4',
    projectId: 'project1',
    parentId: 'feedback1',
    content: 'We are planning to start implementation by the end of next month once training is completed.',
    type: 'response',
    createdAt: '2025-07-31T11:45:00Z',
    createdBy: {
      id: '5',
      name: 'David Miller',
      role: UserRole.FACILITY_USER,
      organization: 'Main Hospital'
    },
    resolved: false
  }
];

const getMockFeedback = (projectId: string): Feedback[] => {
  return mockFeedbackData.filter(feedback => feedback.projectId === projectId);
};
